import {
  PRODUCT_DETAIL_TYPE_INGREDIENTS,
  PRODUCT_DETAIL_VALUE_MAX_LENGTH,
  type ProductDetailKindCode,
} from '@wapharma/domain';

export type ProductDetailTypeRow = {
  id: string;
  code: string;
  name: string;
  kind?: string;
  sortOrder?: number;
  isActive?: boolean;
};

export type ProductFicheDetailDraft = {
  rowKey: string;
  typeId: string;
  typeCode: string;
  typeName: string;
  kind: ProductDetailKindCode;
  value: string;
  collapsed?: boolean;
};

export const PRODUCT_DETAIL_KIND_OPTIONS: Array<{ value: ProductDetailKindCode; label: string }> = [
  { value: 'TEXT', label: 'Texte' },
  { value: 'LIST', label: 'Liste' },
  { value: 'NUMBER', label: 'Nombre' },
];

export function parseProductDetailKind(kind: string | undefined | null): ProductDetailKindCode {
  const k = (kind ?? 'TEXT').trim().toUpperCase();
  if (k === 'LIST' || k === 'NUMBER') return k;
  return 'TEXT';
}

export function productDetailKindLabel(kind: string | undefined | null): string {
  const parsed = parseProductDetailKind(kind);
  return PRODUCT_DETAIL_KIND_OPTIONS.find((o) => o.value === parsed)?.label ?? 'Texte';
}

export function isIngredientsDetailType(code: string | undefined): boolean {
  return (code ?? '').trim().toUpperCase() === PRODUCT_DETAIL_TYPE_INGREDIENTS;
}

export function unusedProductDetailTypes(
  types: ProductDetailTypeRow[],
  rows: ProductFicheDetailDraft[],
): ProductDetailTypeRow[] {
  const used = new Set(rows.map((r) => r.typeId).filter(Boolean));
  return types.filter((t) => t.isActive !== false && !used.has(t.id));
}

/** Wizard type picks: Ingrédients first, then sortOrder / name. */
export function preferIngredientsFirst(types: ProductDetailTypeRow[]): ProductDetailTypeRow[] {
  return [...types].sort((a, b) => {
    const ai = isIngredientsDetailType(a.code) ? 0 : 1;
    const bi = isIngredientsDetailType(b.code) ? 0 : 1;
    if (ai !== bi) return ai - bi;
    const so = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    if (so !== 0) return so;
    return a.name.localeCompare(b.name, 'fr');
  });
}

export function draftFromPrefillDetails(
  types: ProductDetailTypeRow[],
  prefill: Array<{ typeCode: string; value: string }> | undefined,
): ProductFicheDetailDraft[] {
  if (!prefill?.length) return [];
  const byCode = new Map(types.map((t) => [t.code.toUpperCase(), t]));
  const out: ProductFicheDetailDraft[] = [];
  for (const row of prefill) {
    const type = byCode.get(row.typeCode.trim().toUpperCase());
    const value = row.value.trim().slice(0, PRODUCT_DETAIL_VALUE_MAX_LENGTH);
    if (!type || !value) continue;
    if (out.some((d) => d.typeId === type.id)) continue;
    out.push({
      rowKey: type.id,
      typeId: type.id,
      typeCode: type.code,
      typeName: type.name,
      kind: parseProductDetailKind(type.kind),
      value,
    });
  }
  return out;
}

export function newDetailDraftRowKey(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `d-${Math.random().toString(36).slice(2)}`;
}

export function emptyDetailDraft(): ProductFicheDetailDraft {
  return {
    rowKey: newDetailDraftRowKey(),
    typeId: '',
    typeCode: '',
    typeName: '',
    kind: 'TEXT',
    value: '',
    collapsed: false,
  };
}

export function listItemsFromValue(raw: string): string[] {
  if (!raw) return [''];
  const parts = raw.split(/\r?\n/);
  return parts.length > 0 ? parts : [''];
}

export function joinListItems(items: string[]): string {
  return items.join('\n').slice(0, PRODUCT_DETAIL_VALUE_MAX_LENGTH);
}

export function draftFromDetailType(type: ProductDetailTypeRow, value = ''): ProductFicheDetailDraft {
  return {
    rowKey: newDetailDraftRowKey(),
    typeId: type.id,
    typeCode: type.code,
    typeName: type.name,
    kind: parseProductDetailKind(type.kind),
    value,
    collapsed: false,
  };
}
